import React, {Fragment} from 'react';
import {Grid, Box, useMediaQuery, Theme} from '@mui/material';
import {useGetList, usePermissions} from 'react-admin';

import {Logo} from '../layout/Logo';
import WeeklyRevenueChart from './WeeklyRevenueChart';
import MonthlyRevenueChart from './MonthlyRevenueChart';
import BestSellingReport from "./BestSellingReport";
import DailyRevenue from './DailyRevenue';
import {QuickViewReport} from "../utils/types";
import StateOfContentReport from "./StateOfContentReport";
import {hasAccess} from "ra-auth-acl";
import DefaultDashboard from "./DefaultDashboard";
import ReturnsDashboard from "./ReturnsDashboard";

interface MarketplaceDailyRevenue {
    marketplace: string;
    revenue: string;
    orders: number;
}

const Spacer = () => <span style={{width: '1em'}}/>;
const Dashboard = () => {
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('md')
    );

    const {permissions} = usePermissions();
    const hasAccessToOrder = hasAccess(permissions, 'order.list');
    const hasAccessToOrderReturns = hasAccess(permissions, 'orderreturn.list');

    const styles = {
        flex: {display: 'flex'},
        flexColumn: {display: 'flex', flexDirection: 'column'},
        leftCol: {flex: 1, marginRight: '0.5em'},
        rightCol: {flex: 1, marginLeft: '0.5em'},
        singleCol: {marginTop: '1em', marginBottom: '1em'},
    };

    const {data: stats, isLoading} = useGetList('quick-view-reports', {
        pagination: {page: 1, perPage: 50},
    });

    if (isLoading) {
        return null;
    }

    const weeklyRevenueReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'WEEKLY_REVENUE');
    const monthlyRevenueReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'MONTHLY_REVENUE');
    const bestSellersReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'BEST_SELLERS');
    const dailyRevenueReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'DAILY_REVENUE');
    const stateOfContentReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'STATE_OF_CONTENT_FOR_MARKETPLACES');
    const returnsReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'RETURNS');
    const dailyMarketplacesRevenueReport = (stats as QuickViewReport[]).find((stat: any) => stat.code === 'MARKETPLACE_DAILY');

    if (!hasAccessToOrder) {
        if (hasAccessToOrderReturns && returnsReport) {
            return <ReturnsDashboard report={returnsReport} />;
        }

        return <DefaultDashboard/>;
    }

    return isSmall ? (
        <Grid container spacing={1}>
            <Box p={2} justifyContent={'center'} width={"100%"} display="flex">
                <Logo/>
            </Box>
            <Grid item xs={12}>
                {dailyRevenueReport ?
                    (dailyRevenueReport.data as MarketplaceDailyRevenue[]).map((item: MarketplaceDailyRevenue, index) => {
                        return (
                            <div key={index} style={{marginBottom: 10}}>
                                <DailyRevenue
                                    money={item.revenue}
                                    quantity={item.orders}
                                    name={item.marketplace}
                                    updatedAt={dailyRevenueReport.updatedAt}
                                />
                                {dailyRevenueReport.data.indexOf(item) !== dailyRevenueReport.data.length - 1 &&
                                    <Spacer/>}
                            </div>
                        );
                    }) : null
                }
                <div style={{marginBottom: 20}}>
                    <StateOfContentReport stats={stateOfContentReport}/>
                </div>
                <div style={{marginBottom: 20}}>
                    <WeeklyRevenueChart stats={weeklyRevenueReport}/>
                </div>
                <div style={{marginBottom: 20}}>
                    <BestSellingReport stats={bestSellersReport}/>
                </div>
                <div style={{marginBottom: 20}}>
                    <MonthlyRevenueChart stats={monthlyRevenueReport}/>
                </div>
            </Grid>
        </Grid>
    ) : (
        <Grid container spacing={2} padding={1}>
            <Box mt={2} ml={-3} display="flex">
                <Logo/>
            </Box>

            <Grid item xs={12}>
                <div style={styles.flex}>
                    {dailyRevenueReport ?
                        (dailyRevenueReport.data as MarketplaceDailyRevenue[]).map((item: MarketplaceDailyRevenue, index) => {
                            return (
                                <Fragment key={index}>
                                    <DailyRevenue
                                        money={item.revenue}
                                        quantity={item.orders}
                                        name={item.marketplace}
                                        updatedAt={dailyRevenueReport.updatedAt}
                                    />
                                    {dailyRevenueReport.data.indexOf(item) !== dailyRevenueReport.data.length - 1 &&
                                        <Spacer/>}
                                </Fragment>
                            );
                        }) : null
                    }
                </div>
            </Grid>
            <Grid item xs={12}>
                <div style={styles.flex}>
                    <div style={styles.leftCol}>
                        <div style={styles.flex}>

                        </div>
                        <div style={styles.singleCol}>
                            <WeeklyRevenueChart stats={weeklyRevenueReport}/>
                        </div>
                        <div style={styles.singleCol}>
                            <MonthlyRevenueChart stats={monthlyRevenueReport}/>
                        </div>
                    </div>
                    <div style={styles.rightCol}>
                        <div style={styles.singleCol}>
                            <StateOfContentReport stats={stateOfContentReport}/>
                        </div>
                        <div style={styles.singleCol}>
                            <BestSellingReport stats={bestSellersReport}/>
                        </div>
                    </div>
                </div>
            </Grid>
        </Grid>
    );
};

export default Dashboard;
